import React, { useEffect, useRef, useCallback, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  FaBold, FaItalic, FaUnderline, FaStrikethrough,
  FaAlignLeft, FaAlignCenter, FaAlignRight,
  FaListUl, FaListOl, FaIndent, FaOutdent, FaEraser, FaFont,
} from 'react-icons/fa';
import { FiChevronDown, FiAlignJustify, FiCode } from 'react-icons/fi';
import useImageResize from './rich-text/useImageResize';
import ImageResizeOverlay from './rich-text/ImageResizeOverlay';
import { contentImageCss } from './rich-text/richTextContentCss';

/**
 * RichTextEditor — contenteditable-based rich text editor.
 * Adapted for portal: tw- prefix, react-icons, no "use client".
 *
 * Props:
 *  - value: HTML string
 *  - onChange: (html: string) => void
 *  - placeholder: string
 *  - className: string
 *  - minHeight: number (px, default 200)
 *  - maxHeight: number (px, default 500)
 *  - readOnly: bool
 *  - grow: bool — fill remaining height (ignores maxHeight)
 *  - onImageUpload: (file: File) => Promise<string> — optional. When provided,
 *      pasted images are uploaded via this callback and inserted using the
 *      returned URL. When omitted, images are inlined as base64 data URLs.
 */

const FONT_SIZES = [10, 11, 12, 13, 14, 16, 18, 20, 24, 28, 32, 36];
const DEFAULT_FONT_SIZE = 14;
const LINE_HEIGHTS = [1, 1.2, 1.4, 1.5, 1.6, 1.75, 2, 2.5, 3];
const DEFAULT_LINE_HEIGHT = 1.5;

const FONT_FAMILIES = [
  { label: 'Default',         value: 'inherit' },
  { label: 'Arial',           value: 'Arial, sans-serif' },
  { label: 'Georgia',         value: 'Georgia, serif' },
  { label: 'Times New Roman', value: "'Times New Roman', serif" },
  { label: 'Courier New',     value: "'Courier New', monospace" },
  { label: 'Verdana',         value: 'Verdana, sans-serif' },
  { label: 'Tahoma',          value: 'Tahoma, sans-serif' },
  { label: 'Trebuchet MS',    value: "'Trebuchet MS', sans-serif" },
];

const TEXT_COLORS = [
  { label: 'Default', value: ''        },
  { label: 'Slate',   value: '#1e293b' },
  { label: 'Gray',    value: '#6b7280' },
  { label: 'Red',     value: '#ef4444' },
  { label: 'Orange',  value: '#f97316' },
  { label: 'Amber',   value: '#f59e0b' },
  { label: 'Green',   value: '#22c55e' },
  { label: 'Teal',    value: '#14b8a6' },
  { label: 'Sky',     value: '#0ea5e9' },
  { label: 'Blue',    value: '#3b82f6' },
  { label: 'Indigo',  value: '#6366f1' },
  { label: 'Violet',  value: '#8b5cf6' },
  { label: 'Pink',    value: '#ec4899' },
  { label: 'Rose',    value: '#f43f5e' },
];

const rgbToHex = (rgb) => {
  if (!rgb || rgb === 'rgba(0, 0, 0, 0)') return '';
  const m = rgb.match(/\d+/g);
  if (!m || m.length < 3) return '';
  return '#' + m.slice(0, 3).map(n => parseInt(n, 10).toString(16).padStart(2, '0')).join('');
};

const RichTextEditor = ({
  value = '',
  onChange,
  placeholder = 'Start typing...',
  className,
  minHeight = 200,
  maxHeight = 500,
  readOnly = false,
  grow = false,
  onImageUpload = null,
}) => {
  const editorRef = useRef(null);
  const containerRef = useRef(null);
  // The exact HTML this editor last emitted. The value→DOM sync below skips it,
  // so a round trip through the parent never replaces innerHTML — which would
  // detach the <img> node the resize overlay is anchored to.
  const lastEmittedRef = useRef(null);
  const savedRangeRef = useRef(null);

  const [activeFormats, setActiveFormats]       = useState({});
  const [currentFontSize, setCurrentFontSize]   = useState(DEFAULT_FONT_SIZE);
  const [currentFont, setCurrentFont]           = useState('inherit');
  const [currentColor, setCurrentColor]         = useState('');
  const [currentLineHeight, setCurrentLineHeight] = useState(DEFAULT_LINE_HEIGHT);

  // Xem/sửa HTML source trực tiếp (nút <> trên toolbar).
  const [htmlMode, setHtmlMode]   = useState(false);
  const [htmlDraft, setHtmlDraft] = useState('');
  const [htmlView, setHtmlView]   = useState('edit'); // 'edit' | 'preview'

  const [sizeOpen,  setSizeOpen]  = useState(false);
  const [fontOpen,  setFontOpen]  = useState(false);
  const [colorOpen, setColorOpen] = useState(false);
  const [lineHOpen, setLineHOpen] = useState(false);

  const sizeRef  = useRef(null);
  const fontRef  = useRef(null);
  const colorRef = useRef(null);
  const lineHRef = useRef(null);

  const sizeDropRef  = useRef(null);
  const fontDropRef  = useRef(null);
  const colorDropRef = useRef(null);
  const lineHDropRef = useRef(null);

  const [sizePos,  setSizePos]  = useState({ top: 0, left: 0 });
  const [fontPos,  setFontPos]  = useState({ top: 0, left: 0 });
  const [colorPos, setColorPos] = useState({ top: 0, left: 0 });
  const [lineHPos, setLineHPos] = useState({ top: 0, left: 0 });

  const calcPos = (ref) => {
    if (!ref.current) return { top: 0, left: 0 };
    const r = ref.current.getBoundingClientRect();
    return { top: r.bottom + 4, left: r.left };
  };

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      const inSize  = sizeRef.current?.contains(e.target)  || sizeDropRef.current?.contains(e.target);
      const inFont  = fontRef.current?.contains(e.target)  || fontDropRef.current?.contains(e.target);
      const inColor = colorRef.current?.contains(e.target) || colorDropRef.current?.contains(e.target);
      const inLineH = lineHRef.current?.contains(e.target) || lineHDropRef.current?.contains(e.target);
      if (!inSize)  setSizeOpen(false);
      if (!inFont)  setFontOpen(false);
      if (!inColor) setColorOpen(false);
      if (!inLineH) setLineHOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const emitChange = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    // The img/br guard keeps an image wrapped in a styled span alive.
    el.querySelectorAll('span').forEach((span) => {
      if (!span.textContent.replace(/\u200b/g, '').trim() && !span.querySelector('img,br')) {
        span.remove();
      }
    });
    lastEmittedRef.current = el.innerHTML;
    onChange?.(el.innerHTML);
  }, [onChange]);

  const image = useImageResize({
    editorRef,
    containerRef,
    emitChange,
    enabled: !readOnly && !htmlMode,
  });
  const { clearSelection: clearImageSelection, selectImage } = image;

  // Sync external value → DOM. Comparing against the last emitted HTML (rather
  // than a one-shot flag) matters when several changes land before React flushes,
  // e.g. holding an arrow key to nudge an image's width — the flag would be spent
  // by the first, and the second would replace innerHTML and detach the selected
  // <img> out from under the resize overlay.
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (value === lastEmittedRef.current) return;
    if (el.innerHTML !== value) {
      el.innerHTML = value || '';
      clearImageSelection();
    }
  }, [value, clearImageSelection]);

  // Bật/tắt HTML source. Vào: nạp innerHTML hiện tại vào textarea. Ra: ghi HTML
  // đã sửa trở lại editor rồi emit (giữ nội dung khi chuyển qua lại / khi lưu).
  const toggleHtmlMode = useCallback(() => {
    if (htmlMode) {
      const el = editorRef.current;
      if (el) el.innerHTML = htmlDraft;
      emitChange();
      setHtmlMode(false);
    } else {
      clearImageSelection();
      setHtmlDraft(editorRef.current?.innerHTML ?? value ?? '');
      setHtmlView('edit');
      setHtmlMode(true);
    }
  }, [htmlMode, htmlDraft, value, emitChange, clearImageSelection]);

  const detectStyles = useCallback(() => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const node = sel.anchorNode;
    const el = node?.nodeType === 3 ? node.parentElement : node;
    if (!el) return;
    const cs = window.getComputedStyle(el);
    const size = parseFloat(cs.fontSize);
    if (size) setCurrentFontSize(Math.round(size));
    const color = cs.color;
    const hex = rgbToHex(color);
    setCurrentColor(hex === '#000000' ? '' : hex);
    const lh = cs.lineHeight;
    if (lh && lh !== 'normal') {
      const lhNum = parseFloat(lh) / (size || 14);
      if (lhNum) setCurrentLineHeight(Math.round(lhNum * 100) / 100);
    }
  }, []);

  const updateActiveFormats = useCallback(() => {
    setActiveFormats({
      bold:                document.queryCommandState('bold'),
      italic:              document.queryCommandState('italic'),
      underline:           document.queryCommandState('underline'),
      strikeThrough:       document.queryCommandState('strikeThrough'),
      insertUnorderedList: document.queryCommandState('insertUnorderedList'),
      insertOrderedList:   document.queryCommandState('insertOrderedList'),
      justifyLeft:         document.queryCommandState('justifyLeft'),
      justifyCenter:       document.queryCommandState('justifyCenter'),
      justifyRight:        document.queryCommandState('justifyRight'),
    });
    detectStyles();
  }, [detectStyles]);

  const execCmd = useCallback((cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
    emitChange();
    updateActiveFormats();
  }, [emitChange, updateActiveFormats]);

  const saveSelection = useCallback(() => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      savedRangeRef.current = sel.getRangeAt(0).cloneRange();
    }
  }, []);

  const restoreSelection = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.focus();
    const saved = savedRangeRef.current;
    if (!saved) return;
    try {
      const sel = window.getSelection();
      if (sel) {
        sel.removeAllRanges();
        sel.addRange(saved);
      }
    } catch {
      /* range may be stale — ignore */
    }
  }, []);

  // Insert an <img> at the saved caret position, then notify change.
  const insertImageElement = useCallback((src) => {
    if (!src) return;
    restoreSelection();
    document.execCommand('insertImage', false, src);
    emitChange();

    // Select it straight away, so a pasted screenshot can be sized without an
    // extra click. Deferred a frame — the node has not been laid out yet, and
    // measuring it now would give a zero-width box.
    requestAnimationFrame(() => {
      const nodes = Array.from(editorRef.current?.querySelectorAll('img') || []);
      const inserted = nodes.reverse()
        .find((img) => img.getAttribute('src') === src || img.src === src);
      if (inserted) selectImage(inserted);
    });
  }, [restoreSelection, emitChange, selectImage]);

  // Upload (if a handler is provided) then insert; otherwise inline as base64.
  const insertImageFile = useCallback(async (file) => {
    if (!file || !file.type?.startsWith('image/')) return;

    if (onImageUpload) {
      try {
        const url = await onImageUpload(file);
        if (url) { insertImageElement(url); return; }
      } catch {
        /* upload failed — fall back to inline base64 below */
      }
    }

    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') insertImageElement(reader.result);
    };
    reader.readAsDataURL(file);
  }, [onImageUpload, insertImageElement]);

  // Collect any image files from a clipboard/drag payload (browsers expose
  // them via either items[] or files[]).
  const extractImageFiles = (dataTransfer) => {
    if (!dataTransfer) return [];
    const fromItems = Array.from(dataTransfer.items || [])
      .filter(it => it.kind === 'file' && it.type.startsWith('image/'))
      .map(it => it.getAsFile())
      .filter(Boolean);
    if (fromItems.length) return fromItems;
    return Array.from(dataTransfer.files || []).filter(f => f.type.startsWith('image/'));
  };

  const wrapSelectionWithStyle = useCallback((styleProp, styleVal, closeFn) => {
    restoreSelection();
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) { closeFn?.(); return; }

    const range = sel.getRangeAt(0);
    const span = document.createElement('span');
    span.style[styleProp] = styleVal;

    if (sel.isCollapsed) {
      span.appendChild(document.createTextNode('​'));
      range.insertNode(span);
      const newRange = document.createRange();
      newRange.setStart(span.firstChild, 1);
      newRange.collapse(true);
      sel.removeAllRanges();
      sel.addRange(newRange);
    } else {
      try {
        range.surroundContents(span);
      } catch {
        const fragment = range.extractContents();
        span.appendChild(fragment);
        range.insertNode(span);
      }
      sel.removeAllRanges();
      const newRange = document.createRange();
      newRange.selectNodeContents(span);
      sel.addRange(newRange);
    }
    emitChange();
    closeFn?.();
  }, [restoreSelection, emitChange]);

  const applyFontSize = useCallback((px) => {
    wrapSelectionWithStyle('fontSize', `${px}px`, () => { setSizeOpen(false); setCurrentFontSize(px); });
  }, [wrapSelectionWithStyle]);

  const applyFontFamily = useCallback((family) => {
    wrapSelectionWithStyle('fontFamily', family, () => { setFontOpen(false); setCurrentFont(family); });
  }, [wrapSelectionWithStyle]);

  const applyColor = useCallback((hex) => {
    restoreSelection();
    if (!hex) {
      execCmd('removeFormat');
      setCurrentColor('');
      setColorOpen(false);
      return;
    }
    wrapSelectionWithStyle('color', hex, () => { setColorOpen(false); setCurrentColor(hex); });
  }, [restoreSelection, wrapSelectionWithStyle, execCmd]);

  const applyLineHeight = useCallback((lh) => {
    wrapSelectionWithStyle('lineHeight', String(lh), () => { setLineHOpen(false); setCurrentLineHeight(lh); });
  }, [wrapSelectionWithStyle]);

  const FORMAT_BUTTONS = [
    { cmd: 'bold',                Icon: FaBold,          title: 'Bold (Ctrl+B)',         toggle: true  },
    { cmd: 'italic',              Icon: FaItalic,        title: 'Italic (Ctrl+I)',       toggle: true  },
    { cmd: 'underline',           Icon: FaUnderline,     title: 'Underline (Ctrl+U)',    toggle: true  },
    { cmd: 'strikeThrough',       Icon: FaStrikethrough, title: 'Strikethrough',         toggle: true  },
    { divider: true },
    { cmd: 'justifyLeft',         Icon: FaAlignLeft,     title: 'Align left',            toggle: true  },
    { cmd: 'justifyCenter',       Icon: FaAlignCenter,   title: 'Align center',          toggle: true  },
    { cmd: 'justifyRight',        Icon: FaAlignRight,    title: 'Align right',           toggle: true  },
    { divider: true },
    { cmd: 'insertUnorderedList', Icon: FaListUl,        title: 'Bullet list',           toggle: true  },
    { cmd: 'insertOrderedList',   Icon: FaListOl,        title: 'Numbered list',         toggle: true  },
    { divider: true },
    { cmd: 'indent',              Icon: FaIndent,        title: 'Indent',                toggle: false },
    { cmd: 'outdent',             Icon: FaOutdent,       title: 'Outdent',               toggle: false },
    { divider: true },
    { cmd: 'removeFormat',        Icon: FaEraser,        title: 'Clear formatting',      toggle: false },
  ];

  const currentFontLabel = FONT_FAMILIES.find((f) => f.value === currentFont)?.label ?? 'Font';

  return (
    <div className={classNames(
      'tw-flex tw-flex-col tw-rounded-lg tw-border tw-border-slate-200 tw-bg-slate-50 tw-overflow-hidden tw-transition-colors focus-within:tw-border-primary-400 focus-within:tw-ring-2 focus-within:tw-ring-primary-500/15',
      className,
    )}>
      {/* Toolbar */}
      {!readOnly && (
        <div className="tw-flex tw-items-center tw-gap-1 tw-px-3 tw-py-2 tw-border-b tw-border-slate-200 tw-bg-gradient-to-r tw-from-primary-50/60 tw-to-slate-50 tw-flex-wrap">

          {htmlMode ? (
            /* Toggle gạt qua lại: Edit (code) ⇄ Preview */
            <div className="tw-inline-flex tw-items-center tw-rounded-md tw-border tw-border-slate-300 tw-bg-white tw-overflow-hidden tw-h-7">
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); setHtmlView('edit'); }}
                className={classNames(
                  'tw-h-full tw-px-2.5 tw-flex tw-items-center tw-gap-1 tw-text-[12px] tw-font-semibold tw-border-0 tw-transition-colors',
                  htmlView === 'edit' ? 'tw-bg-primary-600 tw-text-white' : 'tw-bg-transparent tw-text-slate-600 hover:tw-bg-slate-100',
                )}
              >
                <FiCode size={12} /> Edit
              </button>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); setHtmlView('preview'); }}
                className={classNames(
                  'tw-h-full tw-px-2.5 tw-flex tw-items-center tw-gap-1 tw-text-[12px] tw-font-semibold tw-border-0 tw-border-l tw-border-slate-300 tw-transition-colors',
                  htmlView === 'preview' ? 'tw-bg-primary-600 tw-text-white' : 'tw-bg-transparent tw-text-slate-600 hover:tw-bg-slate-100',
                )}
              >
                <i className="bi bi-eye" /> Preview
              </button>
            </div>
          ) : (
          <>
          {/* Font family */}
          <div ref={fontRef} className="tw-relative">
            <button
              type="button"
              title="Font family"
              onMouseDown={(e) => { e.preventDefault(); saveSelection(); setFontPos(calcPos(fontRef)); setFontOpen(o => !o); setSizeOpen(false); setColorOpen(false); setLineHOpen(false); }}
              className="tw-h-7 tw-px-2 tw-flex tw-items-center tw-gap-1 tw-rounded-md tw-border tw-border-slate-200 tw-bg-white tw-text-slate-700 tw-text-xs hover:tw-border-primary-400 hover:tw-text-primary-700 tw-transition-colors tw-max-w-[110px]"
            >
              <FaFont size={10} className="tw-text-slate-400" />
              <span className="tw-truncate">{currentFontLabel}</span>
              <FiChevronDown size={10} className="tw-flex-shrink-0 tw-text-slate-400" />
            </button>
            {fontOpen && (
              <div
                ref={fontDropRef}
                style={{ position: 'fixed', top: fontPos.top, left: fontPos.left, zIndex: 99999, maxHeight: 240, overflowY: 'auto', width: 180 }}
                className="tw-bg-white tw-border tw-border-slate-200 tw-rounded tw-shadow-xl tw-py-1"
              >
                {FONT_FAMILIES.map((f) => (
                  <button
                    key={f.value}
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); applyFontFamily(f.value); }}
                    style={{ fontFamily: f.value !== 'inherit' ? f.value : undefined }}
                    className={classNames(
                      'tw-w-full tw-px-3 tw-py-1.5 tw-text-left tw-text-sm tw-transition-colors hover:tw-bg-primary-50 hover:tw-text-primary-700',
                      currentFont === f.value ? 'tw-text-primary-700 tw-font-semibold tw-bg-primary-50' : 'tw-text-slate-700',
                    )}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Font size */}
          <div ref={sizeRef} className="tw-relative">
            <button
              type="button"
              title="Font size"
              onMouseDown={(e) => { e.preventDefault(); saveSelection(); setSizePos(calcPos(sizeRef)); setSizeOpen(o => !o); setFontOpen(false); setColorOpen(false); setLineHOpen(false); }}
              className="tw-h-7 tw-min-w-[52px] tw-px-2 tw-flex tw-items-center tw-gap-1 tw-rounded-md tw-border tw-border-slate-200 tw-bg-white tw-text-slate-700 tw-text-xs hover:tw-border-primary-400 hover:tw-text-primary-700 tw-transition-colors"
            >
              <span>{currentFontSize}px</span>
              <FiChevronDown size={10} className="tw-text-slate-400" />
            </button>
            {sizeOpen && (
              <div
                ref={sizeDropRef}
                style={{ position: 'fixed', top: sizePos.top, left: sizePos.left, zIndex: 99999, maxHeight: 220, overflowY: 'auto', width: 80 }}
                className="tw-bg-white tw-border tw-border-slate-200 tw-rounded tw-shadow-xl tw-py-1"
              >
                {FONT_SIZES.map((size) => (
                  <button
                    key={size}
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); applyFontSize(size); }}
                    className={classNames(
                      'tw-w-full tw-px-3 tw-py-1 tw-text-left tw-text-xs tw-transition-colors hover:tw-bg-primary-50 hover:tw-text-primary-700',
                      currentFontSize === size ? 'tw-text-primary-700 tw-font-semibold tw-bg-primary-50' : 'tw-text-slate-700',
                    )}
                  >
                    {size}px
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Line height */}
          <div ref={lineHRef} className="tw-relative">
            <button
              type="button"
              title="Line height"
              onMouseDown={(e) => { e.preventDefault(); saveSelection(); setLineHPos(calcPos(lineHRef)); setLineHOpen(o => !o); setSizeOpen(false); setFontOpen(false); setColorOpen(false); }}
              className="tw-h-7 tw-min-w-[52px] tw-px-2 tw-flex tw-items-center tw-gap-1 tw-rounded-md tw-border tw-border-slate-200 tw-bg-white tw-text-slate-700 tw-text-xs hover:tw-border-primary-400 hover:tw-text-primary-700 tw-transition-colors"
            >
              <FiAlignJustify size={11} className="tw-text-slate-400" />
              <span>{currentLineHeight}</span>
              <FiChevronDown size={10} className="tw-text-slate-400" />
            </button>
            {lineHOpen && (
              <div
                ref={lineHDropRef}
                style={{ position: 'fixed', top: lineHPos.top, left: lineHPos.left, zIndex: 99999, maxHeight: 220, overflowY: 'auto', width: 80 }}
                className="tw-bg-white tw-border tw-border-slate-200 tw-rounded tw-shadow-xl tw-py-1"
              >
                {LINE_HEIGHTS.map((lh) => (
                  <button
                    key={lh}
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); applyLineHeight(lh); }}
                    className={classNames(
                      'tw-w-full tw-px-3 tw-py-1 tw-text-left tw-text-xs tw-transition-colors hover:tw-bg-primary-50 hover:tw-text-primary-700',
                      currentLineHeight === lh ? 'tw-text-primary-700 tw-font-semibold tw-bg-primary-50' : 'tw-text-slate-700',
                    )}
                  >
                    {lh}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Color picker */}
          <div ref={colorRef} className="tw-relative">
            <button
              type="button"
              title="Text color"
              onMouseDown={(e) => { e.preventDefault(); saveSelection(); setColorPos(calcPos(colorRef)); setColorOpen(o => !o); setSizeOpen(false); setFontOpen(false); setLineHOpen(false); }}
              className="tw-h-7 tw-w-8 tw-flex tw-flex-col tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-slate-200 tw-bg-white hover:tw-border-primary-400 tw-transition-colors tw-gap-0.5"
            >
              <FaFont size={10} className="tw-text-slate-700" />
              <div
                className="tw-w-4 tw-h-1 tw-rounded-sm tw-border tw-border-slate-300"
                style={{ backgroundColor: currentColor || '#1e293b' }}
              />
            </button>
            {colorOpen && (
              <div
                ref={colorDropRef}
                style={{ position: 'fixed', top: colorPos.top, left: colorPos.left, zIndex: 99999 }}
                className="tw-bg-white tw-border tw-border-slate-200 tw-rounded tw-shadow-xl tw-p-2.5 tw-min-w-[180px]"
              >
                <p className="tw-text-[10px] tw-font-bold tw-text-primary-700 tw-uppercase tw-tracking-wider tw-mb-1.5 tw-px-0.5">Text color</p>
                <div className="tw-grid tw-grid-cols-7 tw-gap-1">
                  {TEXT_COLORS.map((c) => (
                    <button
                      key={c.value || 'default'}
                      type="button"
                      title={c.label}
                      onMouseDown={(e) => { e.preventDefault(); applyColor(c.value); }}
                      className={classNames(
                        'tw-w-6 tw-h-6 tw-rounded-sm tw-border tw-transition-transform hover:tw-scale-110',
                        !c.value ? 'tw-border-dashed tw-border-slate-400 tw-bg-slate-50' : 'tw-border-slate-200',
                        currentColor === c.value && 'tw-ring-2 tw-ring-primary-500 tw-ring-offset-1',
                      )}
                      style={{ backgroundColor: c.value || undefined }}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="tw-w-px tw-h-5 tw-bg-slate-200 tw-mx-1" />

          {/* Format buttons */}
          {FORMAT_BUTTONS.map((btn, i) =>
            btn.divider ? (
              <div key={`div-${i}`} className="tw-w-px tw-h-5 tw-bg-slate-200 tw-mx-0.5" />
            ) : (
              <button
                key={btn.cmd}
                type="button"
                title={btn.title}
                onMouseDown={(e) => { e.preventDefault(); execCmd(btn.cmd); }}
                className={classNames(
                  'tw-h-7 tw-w-7 tw-flex tw-items-center tw-justify-center tw-rounded-md tw-text-slate-600 tw-text-xs tw-transition-colors hover:tw-bg-slate-200 hover:tw-text-slate-900 tw-border-0 tw-bg-transparent',
                  btn.toggle && activeFormats[btn.cmd] && 'tw-bg-primary-100 tw-text-primary-700',
                )}
              >
                <btn.Icon size={12} />
              </button>
            )
          )}
          </>
          )}

          {/* HTML source toggle (<>) */}
          <button
            type="button"
            title={htmlMode ? 'Back to visual editor' : 'Edit HTML source'}
            onMouseDown={(e) => { e.preventDefault(); toggleHtmlMode(); }}
            className={classNames(
              'tw-ml-auto tw-h-7 tw-px-2 tw-flex tw-items-center tw-gap-1 tw-rounded-md tw-text-xs tw-border tw-transition-colors',
              htmlMode
                ? 'tw-bg-primary-100 tw-text-primary-700 tw-border-primary-300'
                : 'tw-text-slate-600 tw-bg-white tw-border-slate-200 hover:tw-border-primary-400 hover:tw-text-primary-700',
            )}
          >
            <FiCode size={13} />
            <span className="tw-font-semibold">HTML</span>
          </button>
        </div>
      )}

      {/* Editable area */}
      <div ref={containerRef} className={classNames('tw-relative tw-bg-white', grow && 'tw-flex-1 tw-min-h-0')}>
        <div
          ref={editorRef}
          contentEditable={!readOnly && !htmlMode}
          suppressContentEditableWarning
          onInput={() => { emitChange(); clearImageSelection(); }}
          {...image.editorHandlers}
          onPaste={(e) => {
            // Images (e.g. screenshots) take priority over text/plain.
            const images = extractImageFiles(e.clipboardData);
            if (images.length) {
              e.preventDefault();
              saveSelection();
              images.forEach(insertImageFile);
              return;
            }
            e.preventDefault();
            const text = e.clipboardData.getData('text/plain');
            if (!text) return;
            const lines = text.split(/\r?\n/);
            const html = lines.map(l => l || '​').join('<br>');
            document.execCommand('insertHTML', false, html);
            emitChange();
          }}
          onDrop={(e) => {
            const images = extractImageFiles(e.dataTransfer);
            if (!images.length) return;
            e.preventDefault();
            // Move the caret to the drop point before inserting.
            const pos = document.caretRangeFromPoint?.(e.clientX, e.clientY);
            if (pos) {
              const sel = window.getSelection();
              sel?.removeAllRanges();
              sel?.addRange(pos);
            }
            saveSelection();
            images.forEach(insertImageFile);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Tab') {
              e.preventDefault();
              execCmd(e.shiftKey ? 'outdent' : 'indent');
            }
          }}
          onKeyUp={() => { saveSelection(); updateActiveFormats(); }}
          onMouseUp={() => { saveSelection(); updateActiveFormats(); }}
          onFocus={updateActiveFormats}
          style={{
            ...(grow
              ? { minHeight, height: '100%', overflowY: 'auto' }
              : { minHeight, maxHeight, overflowY: 'auto' }),
            ...(htmlMode ? { display: 'none' } : {}),
          }}
          className={classNames(
            'tw-w-full tw-px-4 tw-py-3 tw-text-sm tw-text-slate-800 tw-outline-none tw-leading-relaxed rte-content',
            readOnly && 'tw-cursor-default tw-select-text',
          )}
          data-placeholder={placeholder}
        />

        {/* HTML source: toggle gạt Edit (code) ⇄ Preview — hiện 1 pane tại 1 thời điểm. */}
        {htmlMode && htmlView === 'edit' && (
          <textarea
            value={htmlDraft}
            onChange={(e) => {
              const v = e.target.value;
              setHtmlDraft(v);
              // Claim it as ours so the value→DOM sync leaves the hidden
              // contentEditable alone while the source is being edited.
              lastEmittedRef.current = v;
              onChange?.(v);
            }}
            spellCheck={false}
            style={grow ? { minHeight, height: '100%' } : { minHeight, maxHeight }}
            className="tw-w-full tw-px-4 tw-py-3 tw-text-[13px] tw-font-mono tw-text-slate-800 tw-bg-slate-50 tw-outline-none tw-resize-none tw-border-0 tw-leading-relaxed"
            placeholder="<p>Paste HTML code here…</p>"
          />
        )}
        {htmlMode && htmlView === 'preview' && (
          <div
            style={grow ? { minHeight, height: '100%', overflowY: 'auto' } : { minHeight, maxHeight, overflowY: 'auto' }}
            className="tw-w-full tw-px-4 tw-py-3 tw-bg-white rte-content"
            // Preview only renders the HTML for display (no script execution). Content
            // is authored by the admin, same as the User Guide read page.
            dangerouslySetInnerHTML={{ __html: htmlDraft || '<p style="color:#94a3b8">Nothing to preview yet.</p>' }}
          />
        )}

        {/* Image selection outline, resize handles and floating toolbar */}
        {image.selectedImg && image.box && image.viewport && image.isVisible && (
          <ImageResizeOverlay
            box={image.box}
            viewport={image.viewport}
            widthPercent={image.widthPercent}
            align={image.align}
            dragPercent={image.dragPercent}
            isDragging={image.isDragging}
            onAlign={image.alignImage}
            onWidthChange={image.setWidthPercent}
            onNudge={image.nudgeWidth}
            onReset={image.resetWidth}
            onDelete={image.deleteImage}
            getHandleProps={image.getHandleProps}
          />
        )}

        <style>{`
          .rte-content:empty:before {
            content: attr(data-placeholder);
            color: #94a3b8;
            pointer-events: none;
          }
          .rte-content ul { list-style-type: disc; padding-left: 1.5rem; margin: 0.5rem 0; }
          .rte-content ol { list-style-type: decimal; padding-left: 1.5rem; margin: 0.5rem 0; }
          .rte-content li { display: list-item; margin: 0.2rem 0; }
          .rte-content p { margin: 0.4rem 0; }
          .rte-content b, .rte-content strong { font-weight: 700; }
          .rte-content i, .rte-content em { font-style: italic; }
          .rte-content u { text-decoration: underline; }
          .rte-content s, .rte-content strike { text-decoration: line-through; }
          .rte-content a { color: #4f46e5; text-decoration: underline; }
          ${contentImageCss('.rte-content')}
          .rte-content img { cursor: pointer; }
        `}</style>
      </div>
    </div>
  );
};

RichTextEditor.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  minHeight: PropTypes.number,
  maxHeight: PropTypes.number,
  readOnly: PropTypes.bool,
  grow: PropTypes.bool,
  onImageUpload: PropTypes.func,
};

export default RichTextEditor;
